import { useCallback } from 'react';
import { Typography, Form, FormInstance, Divider, Checkbox, Grid } from 'antd';
import { useTranslation } from 'react-i18next';
import { CategoryStepUIConfig } from '../../../shared/CategoryStepUIConfig';
import { MultipleChoiceQuestionUIConfig } from '../../../shared/MultipleChoiceQuestionUIConfig';
import { Answers } from '../../../shared/VolunteerApplicationServiceTypes';
import { ApplicantAnswerToQuestion } from '../ApplicantAnswerToQuestion';
import { ReviewAction } from '../../../shared/Constants';

const { Text } = Typography;
const { useBreakpoint } = Grid;

interface ApplicantAnswersToCategoryStepProps {
	uiConfig: CategoryStepUIConfig;
	form: FormInstance;
	answers?: Answers;
	showCheckbox?: boolean;
	disable?: boolean;
}

const SectionHeader = ({
	heading,
	description,
}: {
	heading: string;
	description: string;
}) => {
	return (
		<div className="hidden sm:flex flex-col mb-[8px]">
			<Text className="font-sans font-bold text-lg text-primary-blue">
				{heading}
			</Text>
			<Text className="font-sans font-normal text-sm mb-[8px]" type="secondary">
				{description}
			</Text>
		</div>
	);
};

export const CategoryApplicantAnswersReviewStep = ({
	uiConfig: { title, subTitle, questions },
	form,
	answers,
	showCheckbox,
	disable,
}: ApplicantAnswersToCategoryStepProps) => {
	const { t } = useTranslation();
	const screens = useBreakpoint();
	const isMobile = !screens.sm;

	const allQuestions = questions.map(
		(question: MultipleChoiceQuestionUIConfig) => {
			const value = answers?.questionsAndAnswers.find(
				(answer) => answer.questionId === question.questionId
			);
			return (
				<div key={question.questionId} className="my-4">
					<ApplicantAnswerToQuestion
						uiConfig={question}
						value={value}
						showBG={isMobile ? true : false}
					/>
				</div>
			);
		}
	);

	const handleConfirmReviewedCheckboxChange = useCallback(
		(checked: boolean) => {
			form.setFieldsValue({
				review: checked ? ReviewAction.APPROVE : undefined,
			});
		},
		[form]
	);

	const handleVideoCallInterviewCheckboxChange = useCallback(
		(checked: boolean) => {
			form.setFieldsValue({
				videoCallCheckbox: checked,
			});
		},
		[form]
	);

	return (
		<div className="flex flex-col h-full min-h-0 max-w-[600px]">
			<Form
				name="basic"
				layout="vertical"
				size="middle"
				labelCol={{ span: 16 }}
				style={{ maxWidth: 600 }}
				initialValues={{ remember: true }}
				autoComplete="off"
				form={form}
			>
				<SectionHeader heading={t(title)} description={t(subTitle)} />
				{allQuestions}
				<Divider className="border border-solid border-gray-200" />
				{showCheckbox && (
					<>
						<Form.Item name="review" valuePropName="checked">
							<Checkbox
								onChange={(e) =>
									handleConfirmReviewedCheckboxChange(e.target.checked)
								}
								disabled={disable}
							>
								{t('iConfirmThatIHaveReviewedTheCandidatesApplication')}
							</Checkbox>
						</Form.Item>
						<Form.Item name="videoCallCheckbox" valuePropName="checked">
							<Checkbox
								onChange={(e) =>
									handleVideoCallInterviewCheckboxChange(e.target.checked)
								}
								disabled={disable}
							>
								{t('iConfirmIWillConductVideoCallInterview')}
							</Checkbox>
						</Form.Item>
					</>
				)}
			</Form>
		</div>
	);
};
